'use client';

import { useState } from 'react';
import {
  SplashPinStepElementContentModel,
  SplashPinStepPropertiesModel,
} from '@/services/platform/api/model';
import ModuleWrapper from '../../../module/wrapper';
import { cn, ModuleProperties, SplashContext, useAuthStore, useClientTranslation } from '@/lib/core';
import { StepPin } from '../step-pin';
import { useNavigate } from 'react-router-dom';

const TOTAL_STEPS = 2;

export const SplashPinStep = (props: ModuleProperties) => {
  const content = props.content as SplashPinStepElementContentModel | undefined;
  const properties = content?.properties as SplashPinStepPropertiesModel;

  const navigate = useNavigate();
  const translation = useClientTranslation();

  const { pin: storePin, setPin: storeSetPin, clearUser } = useAuthStore();

  const ctx = props.context as SplashContext | undefined;
  const isVerifyMode = storePin !== null;
  const biometryKind = ctx?.biometryKind ?? 'face';
  const useBiometricLabel = ctx?.onUseBiometric
    ? biometryKind === 'face'
      ? (properties?.enterPinUseFaceIdLabel ?? translation('App.Labels.UseFaceId'))
      : (properties?.enterPinUseFingerprintLabel ?? translation('App.Labels.UseFingerprint'))
    : undefined;

  const [step, setStep] = useState<1 | 2>(1);
  const [firstPin, setFirstPin] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  if (!content) return null;

  const setPinTitle = properties?.setPinTitle;
  const setPinDescription = properties?.setPinDescription;
  const setPinError = properties?.setPinError;

  const confirmPinTitle = properties?.confirmPinTitle;
  const confirmPinDescription = properties?.confirmPinDescription;
  const confirmPinError = properties?.confirmPinError;
  const confirmPinRedefineLabel = properties?.confirmPinForgotLabel;

  const verifyPinTitle = properties?.enterPinTitle;
  const verifyPinDescription = properties?.enterPinWelcomeText;
  const verifyPinError = properties?.enterPinError;
  const verifyPinForgotLabel = properties?.enterPinForgotLabel;

  const handleChange = (value: string) => {
    setPin(value);
    setError('');
  };

  const handleComplete = (value: string) => {
    if (isVerifyMode) {
      if (Number(value) === storePin) {
        (props.context as SplashContext)?.onPin?.();
      } else {
        setError(verifyPinError ?? '');
        setPin('');
      }
      return;
    }

    if (step === 1) {
      setFirstPin(value);
      setPin('');
      setStep(2);
    } else {
      if (value === firstPin) {
        storeSetPin(Number(value));
        (props.context as SplashContext)?.onPin?.();
      } else {
        setError(confirmPinError ?? '');
        setPin('');
      }
    }
  };

  const handleRedefine = () => {
    setStep(1);
    setFirstPin('');
    setPin('');
    setError('');
  };

  const handleForgot = () => {
    clearUser();
    navigate('/');
  };

  const currentTitle = isVerifyMode ? verifyPinTitle : step === 2 ? confirmPinTitle : setPinTitle;
  const currentDescription = isVerifyMode
    ? verifyPinDescription
    : step === 2
      ? confirmPinDescription
      : setPinDescription;
  const stepper = !isVerifyMode ? { current: step, total: TOTAL_STEPS } : undefined;
  // Suppress unused-variable warnings for setPinError (reserved for future use)
  void setPinError;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['step__pin', 'justify-start!', 'min-h-[calc(100vh)] p-4 bg-white']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex', 'flex-col', 'flex-1', 'justify-between', 'gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <StepPin
        isVerifyMode={isVerifyMode}
        inputKey={step}
        title={currentTitle}
        description={currentDescription}
        error={error}
        stepper={stepper}
        forgotPinLabel={isVerifyMode ? verifyPinForgotLabel : undefined}
        onForgotPin={isVerifyMode ? handleForgot : undefined}
        useBiometricLabel={isVerifyMode ? useBiometricLabel : undefined}
        onUseBiometric={isVerifyMode ? ctx?.onUseBiometric : undefined}
        redefineLabel={!isVerifyMode && step === 2 ? confirmPinRedefineLabel : undefined}
        onRedefine={!isVerifyMode && step === 2 ? handleRedefine : undefined}
        pin={pin}
        onChange={handleChange}
        onComplete={handleComplete}
      />
    </ModuleWrapper>
  );
};
